import type { AssetLibrary } from "@quay/contracts";

export const API_URL = (import.meta.env.VITE_API_URL ?? "/api/v2")
  .replace(/\/$/, "")
  .replace(/\/api\/v1$/, "/api/v2");

/** Resolve persisted asset/audio paths in both local and deployed builds.
 * VITE_API_URL may be absolute or browser-relative; the default is same-origin. */
export function resolveApiUri(uri: string): string {
  try {
    const browserBase =
      typeof window === "undefined"
        ? "http://localhost/"
        : window.location.href;
    return new URL(uri, new URL(API_URL, browserBase)).href;
  } catch {
    // Keep malformed/external values visible to the loader so it can report the
    // actual resource failure instead of failing while resolving the URI.
    return uri;
  }
}

export type AssetRecord = {
  library?: AssetLibrary | null;
  assetId: string;
  revision: number;
  displayName: string;
  description: string;
  category: string;
  content: {
    uri: string;
    sha256: string;
    byteLength: number;
    mediaType: "model/gltf-binary";
  };
  inspection: {
    explicitFront?: "+X" | "-X" | "+Z" | "-Z" | null;
    sizeSource?: "measured" | "user" | null;
    status: "pending" | "complete" | "failed";
    rawBounds: {
      min: [number, number, number];
      max: [number, number, number];
    } | null;
    canonicalSize: [number, number, number] | null;
    normalizationScale: [number, number, number] | null;
    baseOffset: [number, number, number];
    forwardCorrection: [number, number, number, number];
  };
  placement: "floor_only" | "support_only" | "floor_or_support";
  supportsObjects: boolean;
  wallAlignment: "left" | "center" | "right" | null;
  availability: "global" | "local";
  roomId: string | null;
};
export type ImageAssetRecord = {
  assetId: string;
  uri: string;
  sha256: string;
  byteLength: number;
  mediaType: "image/png" | "image/jpeg" | "image/webp";
  displayName: string;
};
export type ContentAssetRecord = {
  assetId: string;
  uri: string;
  sha256: string;
  byteLength: number;
  mediaType:
    | "application/pdf"
    | "text/html"
    | "audio/mpeg"
    | "audio/wav"
    | "video/mp4"
    | "video/webm"
    | "text/vtt";
  displayName: string;
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function checked(response: Response): Promise<any> {
  const value = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new ApiError(
      value.error?.message ?? `HTTP ${response.status}`,
      response.status,
      value.error?.code ?? "HTTP_ERROR",
      value.error?.details ?? {},
    );
  return value;
}
export const api = {
  health: async (
    signal?: AbortSignal,
  ): Promise<{ status: string; version: string }> =>
    checked(
      await fetch(`${API_URL}/health`, {
        ...(signal ? { signal } : {}),
      }),
    ),
  assets: async (projectId?: string): Promise<AssetRecord[]> => {
    const items: AssetRecord[] = [];
    let cursor = 0;
    for (let page = 0; page < 51; page++) {
      const params = new URLSearchParams({
        limit: "200",
        cursor: String(cursor),
      });
      if (projectId) params.set("project_id", projectId);
      const response = await checked(
        await fetch(`${API_URL}/assets?${params}`),
      );
      if (!Array.isArray(response.items))
        throw new Error("Invalid asset catalog page");
      items.push(...response.items);
      if (response.nextCursor == null) return items;
      if (
        !Number.isInteger(response.nextCursor) ||
        response.nextCursor <= cursor
      )
        throw new Error("Invalid asset catalog cursor");
      cursor = response.nextCursor;
    }
    throw new Error("Asset catalog pagination budget exceeded");
  },
  upload: async (
    file: File,
    fields: { displayName: string; description: string; category: string },
  ): Promise<AssetRecord> => {
    const body = new FormData();
    // Keep multipart parsers out of unusual user-controlled filename syntax.
    const uploadName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
    body.append("file", file, uploadName);
    body.append("display_name", fields.displayName);
    body.append("description", fields.description);
    body.append("category", fields.category);
    return checked(
      await fetch(`${API_URL}/assets`, {
        method: "POST",
        body,
      }),
    );
  },
  getAsset: async (id: string): Promise<AssetRecord> =>
    checked(await fetch(`${API_URL}/assets/${encodeURIComponent(id)}`)),
  talkingAsset: async (
    id: string,
    expectedRevision: number,
    name: string,
  ): Promise<AssetRecord> =>
    checked(
      await fetch(`${API_URL}/assets/${id}/talking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ expectedRevision, name }),
      }),
    ),
  uploadImage: async (file: File): Promise<ImageAssetRecord> => {
    const body = new FormData();
    const uploadName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
    body.append("file", file, uploadName);
    body.append("display_name", file.name.slice(0, 200));
    return checked(
      await fetch(`${API_URL}/backdrops`, {
        method: "POST",
        body,
      }),
    );
  },
  uploadContent: async (file: File): Promise<ContentAssetRecord> => {
    const body = new FormData();
    body.append("file", file, file.name.replace(/[^a-zA-Z0-9._-]+/g, "_"));
    body.append("display_name", file.name.slice(0, 200));
    return checked(await fetch(`${API_URL}/content`, { method: "POST", body }));
  },
  contentAssets: async (): Promise<ContentAssetRecord[]> =>
    (await checked(await fetch(`${API_URL}/content`))).items,
  patchAsset: async (id: string, patch: object): Promise<AssetRecord> =>
    checked(
      await fetch(`${API_URL}/assets/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }),
    ),
  deleteAsset: async (id: string): Promise<void> => {
    await checked(await fetch(`${API_URL}/assets/${id}`, { method: "DELETE" }));
  },
  plan: async (body: object, signal: AbortSignal): Promise<any> =>
    checked(
      await fetch(`${API_URL}/plans`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal,
      }),
    ),
};
